import { Injectable } from '@angular/core';
import {
DataFromCSV,
GatewayResponse,
GetMaterialDataFromCSVRequest,
MaterialAddRequest,
MaterialDeleteRequest,
MaterialEditRequest,
MaterialGetDescriptionRequest,
MaterialGetPagingRequest,
SortDirection
} from '@dave/types';
import { MaterialFilterType,MaterialSortProperty } from '@dave/types/dist/proto/erp/material';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom,Observable, of,ReplaySubject } from 'rxjs';
import { distinctUntilChanged,map } from 'rxjs/operators';
import { isNotNullOrUndefined } from '../../helper/helper';
import { MaterialEntity,MaterialEntityFromBackend } from '../entities/material.entity';
import { State } from '../State';
import { BaseActionTypes } from '../State/actions/base.actions';
import { MaterialActionTypes } from '../State/actions/material.actions';
import { AppGatewayService } from './app-gateway.service';

const BUFFER_TIME = 10 * 60 * 1000;

// must pass JSON.stringify
export type materialBackendFilter = {
    withDeleted?: boolean;
    withSupplier?: boolean;
    withMaterialToSuppliers?: boolean;
    ids?: number[];
    [MaterialFilterType.SupplierFilter]?: number[];
    [MaterialFilterType.MaterialGroupTypeFilter]?: number[];
    [MaterialFilterType.MaterialMainGroupTypeFilter]?: number[];
};

enum ErrorCodes {
    LoadDescription = 'Beschreibung Abrufen fehlgeschlagen',
    Load = 'Material Abrufen fehlgeschlagen',
    ParseDataFromCsv = 'Handscannerdaten lesen fehlgeschlagen'
}

@Injectable({
    providedIn: 'root',
})
export class MaterialDataService {
    private cache = new Map<number, ReplaySubject<MaterialEntity>>();

    constructor(private store$: Store<State>, private gatewayService: AppGatewayService, private actions$: Actions) {}
    getDescription$(materialId: number): Promise<string> {
        const requestPayload: MaterialGetDescriptionRequest = {
            Id: materialId && materialId + '',
        };

        const requests$ = new Promise<{ ErpMaterialGetDescription: Partial<GatewayResponse['ErpMaterialGetDescription']>; Errors: GatewayResponse['Errors'] }>(async (resolve, reject) => {
            let results: GatewayResponse['ErpMaterialGetDescription']['Description'] = '';
            let errors = {};
            const res = await this.gatewayService.Request({
                ErpMaterialGetDescription: {
                    ...requestPayload,
                },
            });
            if (res.ErpMaterialGetDescription?.Description) {
                results = res.ErpMaterialGetDescription?.Description;
            }
            if (res.Errors) {
                errors = { ...errors, ...res.Errors };
            }

            resolve({ ErpMaterialGetDescription: { Description: results }, Errors: errors });
        });
        return requests$
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    return res.ErpMaterialGetDescription.Description ? res.ErpMaterialGetDescription.Description : '';
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.LoadDescription,
                            Err: err,
                        },
                    }),
                );
                return '';
            });
    }

    getMaterialById$(id: number): Observable<MaterialEntity> {
        console.log('getMaterialById$', id)
        if (!this.cache.has(id)) {
            // this.cache.set(id, new ReplaySubject<MaterialEntity>(1, BUFFER_TIME)); //todo buffertime so einbauen das das ding neu abgerufen wird wenn man nach der buffer size wieder drauf zugreift
            this.cache.set(id, new ReplaySubject<MaterialEntity>(1));
            this.getMaterials(0, null, null, null, null, { ids: [id] }).then((v) => this.cache.get(id).next(v.items[0]));
        }
        return this.cache.get(id).pipe(distinctUntilChanged((a, b) => a.UpdatedAt.getTime() === b.UpdatedAt.getTime()));
        // return from(this.getMaterials(0, null, null, null, null, { ids: [id] })).pipe(
        //     map((ret) => ret.items[0]),
        //     shareReplay({ refCount: true, bufferSize: 1 }),
        // );
    }
    refreshCache(materials: MaterialEntity[]) {
        console.log('refreshCache', materials)
        materials.forEach(material => {
            if (this.cache.has(material.Id)) {
                console.log('this.cache.has(material.Id)', material)

                this.cache.get(material.Id).next(material);
            }
        });
    }
    reloadCache(ids: number[]) {
        const cachedIds = ids.filter(id => this.cache.has(id));
        if (cachedIds.length > 0) {
            this.getMaterialsById$(cachedIds, false);
        }
    }
    getMaterialsById$(ids: number[], useCache = true): Observable<MaterialEntity[]> {
        let allInCache = true;
        if (ids.length === 0) {
            console.error('getMaterialsById$ ids.length === 0')
            return of([]);
        }
        ids.forEach((id) => {
            if (!this.cache.has(id)) {
                allInCache = false;
                this.cache.set(id, new ReplaySubject<MaterialEntity>(1));
            }
        });
        if (!allInCache || !useCache) {
            this.getMaterials(0, null, null, null, null, { ids }).then((ret) => {
                ret.items.forEach((item) => {
                    const itemCache = this.cache.get(item.Id);
                    itemCache.next(item);
                    // todo ggf auf Subject und Observable mit shareReplay umbauen, damit die Einträge aus der map keine values haben wenn die keiner sunbscribed
                    // todo eine methode überlegen um die cahce map wieder leer zu bekommen
                    // if (!itemCache.value || item.UpdatedAt.getTime() !== itemCache.value.UpdatedAt.getTime()) {
                    //     itemCache.next(item);
                    // }
                });
                /**
                 * für gelöschte
                 */
                ids.forEach((id) => {
                    if (ret.items.findIndex((item) => item.Id === id) === -1) {
                        const itemCache = this.cache.get(id);
                        itemCache.next(null)
                    }
                })
            });
        }
        return combineLatest(ids.map((id) => this.cache.get(id).pipe(distinctUntilChanged((a, b) => a?.UpdatedAt.getTime() === b?.UpdatedAt.getTime())))).pipe(map(res => res.filter(isNotNullOrUndefined)));

    }
    getMaterials(
        offset: number,
        pageSize: number,
        searchQuery?: string,
        sortDirection?: 'asc' | 'desc',
        sortProperty?: MaterialSortProperty,
        filterObject?: materialBackendFilter,
    ): Promise<{
        items: MaterialEntity[];
        length: number;
        hasError: boolean;
        isCountLimitReached: boolean;
    }> {
        let requestPayload: MaterialGetPagingRequest;
        try {
            const filter: MaterialGetPagingRequest['Filter'] = [];
            if (filterObject) {
                if (filterObject[MaterialFilterType.SupplierFilter]) {
                    filter.push({ FilterKey: MaterialFilterType.SupplierFilter, Ids: filterObject[MaterialFilterType.SupplierFilter].map((id) => id.toString()) });
                }
                if (filterObject[MaterialFilterType.MaterialGroupTypeFilter]) {
                    filter.push({ FilterKey: MaterialFilterType.MaterialGroupTypeFilter, Ids: filterObject[MaterialFilterType.MaterialGroupTypeFilter].map((id) => id.toString()) });
                }
                if (filterObject[MaterialFilterType.MaterialMainGroupTypeFilter]) {
                    filter.push({ FilterKey: MaterialFilterType.MaterialMainGroupTypeFilter, Ids: filterObject[MaterialFilterType.MaterialMainGroupTypeFilter].map((id) => id.toString()) });
                }
            }
            requestPayload = {
                Limit: isNotNullOrUndefined(pageSize) ? pageSize + '' : null,
                Offset: offset + '',
                SearchQuery: searchQuery,
                SortDirectionWrapper: { SortDirection: sortDirection === 'desc' ? SortDirection.desc : SortDirection.asc },
                SortPropertyWrapper: sortProperty ? { SortProperty: sortProperty } : null,
                Filter: filter.length ? filter : undefined,
                Ids: filterObject?.ids?.length ? filterObject.ids.map((id) => id.toString()) : undefined,
                // PartnerId?: string | undefined
                WithDeleted: !!filterObject?.withDeleted,
                WithSupplierIds: !!filterObject?.withSupplier,
                WithMaterialToSuppliers: !!filterObject?.withMaterialToSuppliers,
            };
        } catch (err) {
            this.store$.dispatch(
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Load,
                        Err: err,
                    },
                }),
            );
            console.log('catch', err);
            return firstValueFrom(of({ length: null, items: [], hasError: true, isCountLimitReached: false }));
        }

        return this.gatewayService
            .Request({
                ErpMaterialGetPaged: {
                    ...requestPayload,
                },
            })
            .then((res: Pick<GatewayResponse, 'ErpMaterialGetPaged' | 'Errors'>) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    return {
                        isCountLimitReached: res.ErpMaterialGetPaged.LimitReached,
                        hasError: false,
                        items: res.ErpMaterialGetPaged.Materials.map((v) => MaterialEntityFromBackend(v)),
                        // PageCount ist nicht die anzahl der Seiten sondern die anzahl der materials
                        length: res.ErpMaterialGetPaged.PageCount ? +res.ErpMaterialGetPaged.PageCount /** pageSize*/ : null,
                    };
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Load,
                            Err: err,
                        },
                    }),
                );
                console.log('catch', err);
                return { length: null, items: [], hasError: true, isCountLimitReached: false };
            });
    }

    changeMaterial(Payload: MaterialEditRequest): Promise<MaterialEntity> {
        return this.gatewayService
            .Request({ ErpMaterialEdit: Payload })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    const m = MaterialEntityFromBackend(res.ErpMaterialEdit.Material);
                    this.refreshCache([m]);
                    this.store$.dispatch(MaterialActionTypes.CreateSuccess({ Payload: m }));
                    return m;
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: 'Material Bearbeiten fehlgeschlagen',
                            Err: err,
                        },
                    }),
                );
                return null;
            });
    }
    addMaterial(Payload: MaterialAddRequest): Promise<MaterialEntity> {
        return this.gatewayService
            .Request({ ErpMaterialAdd: Payload })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    const m = MaterialEntityFromBackend(res.ErpMaterialAdd.Material);
                    this.refreshCache([m]);
                    return m
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: 'Material Hinzufügen fehlgeschlagen',
                            Err: err,
                        },
                    }),
                );
                return null;
            });
    }
    deleteMaterial(Payload: MaterialDeleteRequest): Promise<MaterialEntity> {
        return this.gatewayService
            .Request({ ErpMaterialDelete: Payload })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    const m = MaterialEntityFromBackend(res.ErpMaterialDelete.Material);
                    this.refreshCache([m]);
                    return m;
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: 'Material Löschen fehlgeschlagen',
                            Err: err,
                        },
                    }),
                );
                return null;
            });
    }

    getWarehouseMovementFromCSV(requestPayload: GetMaterialDataFromCSVRequest): Promise<DataFromCSV[]> {
        const request$ = this.gatewayService.Request({
            ErpDataFromCSVGet: {
                ...requestPayload,
            },
        });
        return request$
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    return res.ErpDataFromCSVGet.DataFromCSV
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.ParseDataFromCsv,
                            Err: err,
                        },
                    }),
                );
                return null;
            });
    }
}
